import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { CONTACT, DEFAULT_SPECIAL_DISHES, fmtPhone, mapsSearch, telHref } from "../data/site";
import { CART_STORAGE_KEY, cartOwner, emptyCart, readCart } from "../lib/cartStorage";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

export const StoreProvider = ({ children }) => {
  const [initialCart] = useState(() => readCart());
  const [settings, setSettings] = useState(null);
  const [specialDishes, setSpecialDishes] = useState(DEFAULT_SPECIAL_DISHES);
  const [cartOpen, setCartOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [cartState, setCartState] = useState(initialCart.cart);
  const [cartNotice, setCartNotice] = useState(
    initialCart.legacyInvalidated
      ? "We cleared an older saved cart because its restaurant could not be verified. Please choose a restaurant and add the items again."
      : ""
  );
  const cart = cartState.items;

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartState));
  }, [cartState]);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/site-content/special-dishes")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length === 4) setSpecialDishes(r.data);
      })
      .catch(() => {});
    if (localStorage.getItem("lk_customer_token")) {
      api.get("/auth/customer/me")
        .then((r) => setCustomer(r.data))
        .catch(() => localStorage.removeItem("lk_customer_token"));
    }
  }, []);

  const loginCustomer = (token, cust) => {
    localStorage.setItem("lk_customer_token", token);
    setCustomer(cust);
    setAuthOpen(false);
  };
  const logoutCustomer = () => {
    localStorage.removeItem("lk_customer_token");
    setCustomer(null);
  };
  const refreshCustomer = useCallback(() =>
    api.get("/auth/customer/me").then((r) => setCustomer(r.data)).catch(() => {}), []);
  const add = (item, restaurant) => {
    if (!restaurant?.id || !restaurant?.slug) return false;
    if (cart.length > 0 && cartState.restaurant_id !== restaurant.id) return false;
    setCartState((current) => {
      const items = current.items;
      const found = items.find((x) => x.id === item.id);
      const nextItems = found
        ? items.map((x) => (x.id === item.id ? { ...x, qty: Math.min(x.qty + 1, 20) } : x))
        : [...items, { id: item.id, name: item.name, price: item.price, veg: item.veg, image_url: item.image_url, qty: 1 }];
      return { ...current, ...cartOwner(restaurant), items: nextItems };
    });
    return true;
  };
  const setQty = (id, qty) =>
    setCartState((current) => {
      const items = qty <= 0
        ? current.items.filter((x) => x.id !== id)
        : current.items.map((x) => (x.id === id ? { ...x, qty: Math.min(qty, 20) } : x));
      return items.length ? { ...current, items } : emptyCart();
    });
  const remove = (id) => setCartState((current) => {
    const items = current.items.filter((x) => x.id !== id);
    return items.length ? { ...current, items } : emptyCart();
  });
  const clear = useCallback(() => setCartState(emptyCart()), []);

  const subtotal = useMemo(() => cart.reduce((s, x) => s + x.price * x.qty, 0), [cart]);
  const count = useMemo(() => cart.reduce((s, x) => s + x.qty, 0), [cart]);

  // wa is null until the client confirms a WhatsApp number and it is set in Admin → Settings.
  const wa = useMemo(() => {
    const n = (settings?.whatsapp || "").replace(/\D/g, "");
    if (!n) return null;
    return (msg = "Hi Lucky's Kitchen! I'd like to make an enquiry.") =>
      `https://wa.me/${n}?text=${encodeURIComponent(msg)}`;
  }, [settings]);

  const contact = useMemo(
    () => ({
      phoneDisplay: fmtPhone(settings?.phone || CONTACT.phone),
      phoneHref: telHref(settings?.phone || CONTACT.phone),
      email: settings?.email || CONTACT.email,
      address: settings?.address || CONTACT.address,
      hours: settings?.hours || CONTACT.hours,
      mapsUrl: settings?.address ? mapsSearch(settings.address) : CONTACT.mapsUrl,
    }),
    [settings]
  );

  const locations = useMemo(
    () => (settings?.locations?.length ? settings.locations : CONTACT.locations),
    [settings]
  );

  const value = {
    settings, specialDishes,
    cart, cartRestaurant: {
      id: cartState.restaurant_id,
      slug: cartState.restaurant_slug,
      display_name: cartState.restaurant_name,
    },
    cartOpen, setCartOpen, add, setQty, remove, clear, subtotal, count,
    cartNotice, dismissCartNotice: () => setCartNotice(""),
    customer, authOpen, setAuthOpen, loginCustomer, logoutCustomer, refreshCustomer,
    wa, contact, locations,
  };
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};
